/**
 * Logo - Spreadsheet Arena
 *
 * Three arena columns (pillars) with horizontal grid lines cutting through,
 * topped by an architrave and resting on a base - merging classical
 * architecture with spreadsheet grid aesthetics.
 */

interface LogoProps {
  size?: number;
  variant?: 'default' | 'light' | 'dark';
}

export function Logo({ size = 24, variant = 'default' }: LogoProps) {
  const color = {
    default: '#8B2332',
    light: '#FFFFFF',
    dark: '#1A1A1A',
  }[variant];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Spreadsheet Arena"
    >
      {/* Architrave - top beam connecting columns */}
      <rect x="2" y="2" width="20" height="2.5" rx="0.5" fill={color} />

      {/* Grid cells - 3 columns x 4 rows with opacity gradient */}
      {/* Column A */}
      <rect x="3" y="5" width="4.5" height="3" rx="0.25" fill={color} />
      <rect
        x="3"
        y="8.5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
        opacity="0.8"
      />
      <rect
        x="3"
        y="12"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
        opacity="0.6"
      />
      <rect
        x="3"
        y="15.5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
        opacity="0.4"
      />

      {/* Column B */}
      <rect x="9.75" y="5" width="4.5" height="3" rx="0.25" fill={color} />
      <rect
        x="9.75"
        y="8.5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
        opacity="0.8"
      />
      <rect
        x="9.75"
        y="12"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
        opacity="0.6"
      />
      <rect
        x="9.75"
        y="15.5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
        opacity="0.4"
      />

      {/* Column C */}
      <rect x="16.5" y="5" width="4.5" height="3" rx="0.25" fill={color} />
      <rect
        x="16.5"
        y="8.5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
        opacity="0.8"
      />
      <rect
        x="16.5"
        y="12"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
        opacity="0.6"
      />
      <rect
        x="16.5"
        y="15.5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
        opacity="0.4"
      />

      {/* Base/pedestal - arena floor */}
      <rect x="2" y="19" width="20" height="2.5" rx="0.5" fill={color} />
    </svg>
  );
}

/**
 * Animated logo for loading states
 *
 * The Intelligent Standard: Frame stays solid (architrave + base),
 * only the data cells have subtle motion - like calculations processing.
 * A diagonal shimmer cascades through the cells.
 */
export function LogoAnimated({ size = 24, variant = 'default' }: LogoProps) {
  const color = {
    default: '#8B2332',
    light: '#FFFFFF',
    dark: '#1A1A1A',
  }[variant];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Loading"
    >
      <style>{`
        @keyframes cellShimmer {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 0.9; }
        }
        .cell-0 { animation: cellShimmer 2.4s ease-in-out infinite; animation-delay: 0s; }
        .cell-1 { animation: cellShimmer 2.4s ease-in-out infinite; animation-delay: 0.15s; }
        .cell-2 { animation: cellShimmer 2.4s ease-in-out infinite; animation-delay: 0.3s; }
        .cell-3 { animation: cellShimmer 2.4s ease-in-out infinite; animation-delay: 0.2s; }
        .cell-4 { animation: cellShimmer 2.4s ease-in-out infinite; animation-delay: 0.35s; }
        .cell-5 { animation: cellShimmer 2.4s ease-in-out infinite; animation-delay: 0.5s; }
        .cell-6 { animation: cellShimmer 2.4s ease-in-out infinite; animation-delay: 0.4s; }
        .cell-7 { animation: cellShimmer 2.4s ease-in-out infinite; animation-delay: 0.55s; }
        .cell-8 { animation: cellShimmer 2.4s ease-in-out infinite; animation-delay: 0.7s; }
        .cell-9 { animation: cellShimmer 2.4s ease-in-out infinite; animation-delay: 0.6s; }
        .cell-10 { animation: cellShimmer 2.4s ease-in-out infinite; animation-delay: 0.75s; }
        .cell-11 { animation: cellShimmer 2.4s ease-in-out infinite; animation-delay: 0.9s; }
      `}</style>

      {/* Architrave - solid, stable frame */}
      <rect x="2" y="2" width="20" height="2.5" rx="0.5" fill={color} />

      {/* Grid cells - diagonal shimmer like data processing */}
      {/* Row 0 */}
      <rect
        className="cell-0"
        x="3"
        y="5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
      />
      <rect
        className="cell-1"
        x="9.75"
        y="5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
      />
      <rect
        className="cell-2"
        x="16.5"
        y="5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
      />

      {/* Row 1 */}
      <rect
        className="cell-3"
        x="3"
        y="8.5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
      />
      <rect
        className="cell-4"
        x="9.75"
        y="8.5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
      />
      <rect
        className="cell-5"
        x="16.5"
        y="8.5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
      />

      {/* Row 2 */}
      <rect
        className="cell-6"
        x="3"
        y="12"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
      />
      <rect
        className="cell-7"
        x="9.75"
        y="12"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
      />
      <rect
        className="cell-8"
        x="16.5"
        y="12"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
      />

      {/* Row 3 */}
      <rect
        className="cell-9"
        x="3"
        y="15.5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
      />
      <rect
        className="cell-10"
        x="9.75"
        y="15.5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
      />
      <rect
        className="cell-11"
        x="16.5"
        y="15.5"
        width="4.5"
        height="3"
        rx="0.25"
        fill={color}
      />

      {/* Base - solid, stable frame */}
      <rect x="2" y="19" width="20" height="2.5" rx="0.5" fill={color} />
    </svg>
  );
}
